import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X, Store, ArrowRight } from "lucide-react";
import { useAuth } from "@/App";

/**
 * OwnerAcquisitionBanner
 * Persistent (dismissable) bar that nudges business owners to add their
 * business. Shows on public / directory / category / news / events pages.
 * Hidden on:
 *   • /dashboard, /admin, /claim/* (owner is already in the funnel)
 *   • For business_owner accounts (they've already signed up)
 *   • For 7 days after dismissal
 *
 * Storage key: "tbc.owner_banner.dismissed_at"  (ISO date string)
 */
const DISMISS_KEY = "tbc.owner_banner.dismissed_at";
const DISMISS_TTL_DAYS = 7;

export default function OwnerAcquisitionBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Don't nag business owners who've already joined
    if (user?.user_type === "business_owner") return;

    // Route-based suppression (avoid on funnel pages)
    const path = window.location.pathname;
    const suppressed =
      path.startsWith("/dashboard") ||
      path.startsWith("/admin") ||
      path.startsWith("/claim/") ||
      path.startsWith("/checkout") ||
      path === "/pricing";
    if (suppressed) return;

    // Respect a recent dismissal
    try {
      const dismissedAt = window.localStorage.getItem(DISMISS_KEY);
      if (dismissedAt) {
        const ageMs = Date.now() - new Date(dismissedAt).getTime();
        if (ageMs < DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000) return;
      }
    } catch (e) {
      // localStorage unavailable — just show
    }

    setVisible(true);
  }, [user]);

  const dismiss = () => {
    setVisible(false);
    try {
      window.localStorage.setItem(DISMISS_KEY, new Date().toISOString());
    } catch (e) {
      // no-op
    }
  };

  const claim = () => {
    // Route them to dashboard; unauthenticated visitors will get bounced to sign-up
    navigate("/dashboard?add=1");
  };

  if (!visible) return null;

  return (
    <div
      className="relative bg-tbc-navy text-white pt-[env(safe-area-inset-top)]"
      role="region"
      aria-label="Add your business"
      data-testid="owner-acquisition-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-1.5 flex items-center gap-3 pl-[max(env(safe-area-inset-left),1rem)] pr-[max(env(safe-area-inset-right),1rem)]">
        <p className="flex-1 text-xs sm:text-sm font-body leading-tight truncate">
          <span className="text-tbc-gold">Own a Black business?</span>{" "}
          <span className="text-white/80">Get listed free.</span>
        </p>
        <button
          onClick={claim}
          className="text-xs sm:text-sm text-tbc-gold hover:text-tbc-gold/80 font-medium underline underline-offset-2 whitespace-nowrap flex-shrink-0"
          data-testid="owner-banner-cta"
        >
          Add my business
        </button>
        <button
          onClick={dismiss}
          className="text-white/50 hover:text-white transition-colors flex-shrink-0"
          aria-label="Dismiss"
          data-testid="owner-banner-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
